// region Store

import {action, computed, observable, React, useContext, _} from '@api'
import {MERCH_SECONDARY_VERSIONS} from '@api/graphql'
import {CatalogItemSidebarDetailsStore, VipLocalStore} from '@modules'
import {SecondaryData, SecondarySection, StoresInstance} from '@stores'
import {differenceInObject} from '@utils'
import {runInAction} from 'mobx'

export enum TABS_TYPE {
  SECONDARIES = 'secondaries',
  ADOPTION = 'adoption'
}

export const DetailsSecondariesContext = React.createContext<DetailsSecondariesStore>(undefined)
export const useDetailsSecondariesStore = () => useContext(DetailsSecondariesContext)

export class DetailsSecondariesStore extends VipLocalStore {
  constructor(stores: StoresInstance,private detailsStore: CatalogItemSidebarDetailsStore) {
    super(stores)
    if (detailsStore) {
      detailsStore.secondariesStore = this
    }
  }

  @computed get data() {
    return this.detailsStore?.data
  }

  @action updateSecondariesData = (id: string, field: string, value: any) => {
    const {dataById} = this
    _.set(dataById[id], field, value)
  }

  @action cancelChanges = () => {
    this.secondaries = _.cloneDeep(this.originalSecondaries)
  }

  @computed get isDirty(): boolean {
    const {originalSecondaries, secondaries} = this,
      original = _.cloneDeep(originalSecondaries),
      updated = _.cloneDeep(secondaries)
    if (original?.length && updated?.length) {
      return !_.isEqual(original, updated)
    }
    return false
  }

  @computed get where() {
    return this.detailsStore?.props?.where
  }

  @computed get tab() {
    return this.detailsStore?.detailDef?.modal?.tabs?.find(t => t.type == TABS_TYPE.SECONDARIES)
  }

  @computed get sortedSecondaries(): Array<SecondaryData> {
    const {data, labelField, secondaries} = this,
      sorted = _.map(secondaries, s => _.get(s, labelField)).sort((a, b) => a.localeCompare(b, 'zh-CN'))
    return sorted.map(s => {
      const secondary = secondaries?.find(secondary => _.get(secondary, labelField) === s)
      // Fix for offline MPV, no secondary has id and hence change to one secondary is getting reflected in all
      return {...secondary, id: secondary.id ?? s}
    }).filter(_.identity)
  }

  @computed get dataById(): { [id: string]: any } {
    return _.keyBy(this.sortedSecondaries, s => s.id)
  }

  @computed get sections(): Array<SecondarySection> {
    const {tab, labelField, sortedSecondaries} = this
    if (tab) {
      return sortedSecondaries?.map(s => ({
        ...tab.secondariesTemplate,
        id: s.id,
        label: `${_.get(s, labelField)}`
      }))
    }
    return []
  }

  @computed get labelField() {
    return this.tab?.secondariesTemplate?.labelField ?? 'name'
  }

  @computed get changedValues() {
    return differenceInObject(this.secondaries, this.originalSecondaries)
  }

  @observable secondaries: Array<any> = []

  private originalSecondaries: Array<any> = []

  @observable loading = true

  _runSecondariesQuery = async () => {
    const {detailsStore} = this
    if (!detailsStore?.visible || !detailsStore?.data?.secondaries) return null

    const {data, data: {secondaries}} = detailsStore
    this.loading = true

    try {
      let response
      if (Array.isArray(secondaries) && secondaries.length) {
        response = secondaries.map(s => ({id: s.id || s.exId, attributes: s.attributes, name: data.name}))
      } else {
        // This will be used when secondaries are not present in ES and is to be pulled from C8
        const {where} = this,
          id = _.first(where)?.value,
          {apollo: {client}} = this

        const variables = {
          where: {
            merchVersion: {
              id
            }
          }
        }

        const {data: {plmMerchSecondaryVersions: secondariesResponse}} = await client.mutate({
          mutation: MERCH_SECONDARY_VERSIONS,
          variables: variables
        })
        response = secondariesResponse
      }

      runInAction(() => {
        this.secondaries = response
        this.originalSecondaries = _.cloneDeep(response)
      })
      // _updateSecondaries(response)
      //   detailsStore.isFirstRender = false
      return response
    } finally {
      this.loading = false
    }
  }
}

// endregion




// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/Modal/DetailsSecondaryStore.ts